import React, { createContext, useContext, ReactNode, useState, useCallback, useMemo } from 'react';
import { Contact } from '@/bryntum-lib/models/Contact.jsx';
import { TUseSwivuContactReturn, useSwivuContact } from '@/react/hooks/entityHooks/useSwivuContact';
import { EntityRecord } from '@/types/entity';

interface EntityContactContextValue extends TUseSwivuContactReturn {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  isSearchOpen: boolean;
  setIsSearchOpen: (open: boolean) => void;
  filteredContacts: Contact[];
  entityRecord: EntityRecord | null;

  // Action handlers
  handleUnassignContact: (contact: Contact) => Promise<void>;
  handleDeleteContact: (contact: Contact) => Promise<void>;
}

const EntityContactContext = createContext<EntityContactContextValue | undefined>(undefined);

interface EntityContactProviderProps {
  children: ReactNode;
  entityRecord: EntityRecord | null;
}

export const EntityContactProvider: React.FC<EntityContactProviderProps> = ({
  children,
  entityRecord,
}) => {

  const contactOperations = useSwivuContact({ entityRecord });

  // UI state
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  // Filter contacts based on search
  const filteredContacts = useMemo(() => {
    if (!searchTerm.trim()) return contactOperations.contacts;
    const searchLower = searchTerm.toLowerCase();
    return contactOperations.contacts.filter((contact: Contact) =>
      contact.name?.toLowerCase().includes(searchLower) ||
      contact.phone?.toLowerCase().includes(searchLower) ||
      contact.email?.toLowerCase().includes(searchLower) ||
      contact.address?.toLowerCase().includes(searchLower)
    );
  }, [contactOperations.contacts, searchTerm]);

  // Action handlers - simplified wrappers
  const handleUnassignContact = useCallback(async (contact: Contact) => {
    await contactOperations.unassignContact(contact.id);
  }, [contactOperations.unassignContact]);

  const handleDeleteContact = useCallback(async (contact: Contact) => {
    await contactOperations.deleteContact(contact.id);
  }, [contactOperations.deleteContact]);

  const contextValue = useMemo(() => ({
    ...contactOperations,
    searchTerm,
    setSearchTerm,
    isSearchOpen,
    setIsSearchOpen,
    filteredContacts,
    entityRecord,
    handleUnassignContact,
    handleDeleteContact,
  }), [
    contactOperations,
    searchTerm,
    isSearchOpen,
    filteredContacts,
    entityRecord,
    handleUnassignContact,
    handleDeleteContact,
  ]);

  return (
    <EntityContactContext.Provider value={contextValue}>
      {children}
    </EntityContactContext.Provider>
  );
};

export const useEntityContactContext = (): EntityContactContextValue => {
  const context = useContext(EntityContactContext);
  if (!context) {
    throw new Error('useEntityContactContext must be used within EntityContactProvider');
  }
  return context;
};
